import { Feature, Point, Position } from "geojson";

import { getEndVertex } from "fond/utils/feature";

import { ALL_PHASES, isOverridePhase, isSinglePhase, OverridePhase, phaseLetters, PhaseSet, SINGLE_PHASES, subsetsOfSize } from "./phases";

/**
 * In electrical networks, the 'phase' of a cable or transformer refers to the specific electrical phases
 * (A, B, or C) that the component is associated with. We can override the solver's default choice of phase
 * at certain places in the network.
 *
 * To do this, we divide the network into 'sections'; these are connected runs of radial wires all having
 * the same number of phases. Each section starts at a 'root' which has more phases than the section it feeds.
 * In our solver, these sections arise in three situations:
 *
 * - A 3-phase section feeding into a 2-phase section,
 * - A 3-phase section feeding into a 1-phase section, or
 * - A 2-phase section feeding into a 1-phase section.
 *
 * Override locations consist of:
 *
 * (i) section roots, where the override chooses which of the parent section's phases the section carries, and
 * (ii) single-phase transformers sitting on >=2-phase wires, where the override chooses the transformer's phase.
 *
 * For (i) there is a solver edge case to be aware of. If there's also a single-phase transformer on a
 * section's root node, then the solver only supports overriding the transformer's phase. The section's
 * own phase cannot be overridden in this case.
 */

export const PHASE_DECISION_TYPES = {
  sectionRoot: "SECTION_ROOT",
  transformer: "TRUNK_TRANSFORMER",
} as const;
export type PhaseDecisionType = (typeof PHASE_DECISION_TYPES)[keyof typeof PHASE_DECISION_TYPES];

export interface PhaseDecisionProperties {
  Phase: OverridePhase;
  DecisionType: PhaseDecisionType;
  FondID: string;
  [key: string]: unknown;
}

/** A maximal run of connected radial wires that all carry the same number of phases. */
interface Section {
  /** The node where the section leaves its parent. */
  rootId: string;
  /** The number of phases carried by every wire in the section. */
  width: 1 | 2;
  /** The phase the solver assigned to the section's wires. */
  solvePhase: OverridePhase;
  /** The section this section leaves from; undefined for sections leaving the 3-phase trunk. */
  parent: Section | undefined;
}

const isTruthyFlag = (value: unknown): boolean => value === "True" || value === true;

const numPhases = (edge: Feature): number => Number(edge.properties?.NumPhases ?? 0);

/** Return primary wire edges that are 'radial'. */
const radialPrimaryEdges = (primaryWires: Feature[]): Feature[] =>
  primaryWires.filter((edge) => !isTruthyFlag(edge.properties?.IsLoop) && !isTruthyFlag(edge.properties?.IsVirtual));

/**
 * Return a function that returns the "head phase count" of a given node.
 *
 * The "head phase count" of a node is the highest phase count among the radial edges feeding it.
 * Every source node is treated as if it has a head phase count of 3, even if no 3-phase wire exists.
 */
const buildHeadPhases = (radialEdges: Feature[], sources: Feature[]): ((nodeId: string) => number) => {
  const maxIncomingPhases = new Map<string, number>();
  for (const edge of radialEdges) {
    const endId = edge.properties?.EndID;
    if (typeof endId === "string") {
      maxIncomingPhases.set(endId, Math.max(maxIncomingPhases.get(endId) ?? 0, numPhases(edge)));
    }
  }

  const sourceIds = new Set(sources.map((source) => source.properties?.FondID).filter((id): id is string => typeof id === "string"));

  return (nodeId) => (sourceIds.has(nodeId) ? 3 : (maxIncomingPhases.get(nodeId) ?? 0));
};

/** Visit every edge reachable downstream of rootId through edges with the given phase count. */
const forEachDownstreamEdge = (
  edgesByStart: Map<string, Feature[]>,
  rootId: string,
  phaseCount: number,
  visit: (edge: Feature, endId: string) => void
): void => {
  const stack = [rootId];
  const seen = new Set<string>(stack);
  while (stack.length > 0) {
    for (const edge of edgesByStart.get(stack.pop()!) ?? []) {
      const endId = edge.properties?.EndID;
      if (numPhases(edge) === phaseCount && typeof endId === "string" && !seen.has(endId)) {
        seen.add(endId);
        visit(edge, endId);
        stack.push(endId);
      }
    }
  }
};

const decisionPoint = (
  nodeId: string,
  phase: OverridePhase,
  decisionType: PhaseDecisionType,
  coordinates: Position
): Feature<Point, PhaseDecisionProperties> => ({
  type: "Feature",
  properties: { Phase: phase, DecisionType: decisionType, FondID: nodeId },
  geometry: { type: "Point", coordinates },
});

/**
 * The phase network derived from the solver output layers.
 *
 * This class is responsible for determining phase decision points, the override values they make take,
 * and the downstream effects on overriding a phase.
 */
export class PhaseNetwork {
  /**
   * Radial primary wire edges.
   *
   * Electrical networks are typically 'radial'; power flows outwards from a source. Electrical networks are not
   * necessarily a tree however: they can contain loops and/or virtual edges. We say an edge is 'radial' if it is
   * not a loop or virtual edge. Phase decisions are only made on radial edges.
   */
  readonly radialEdges: Feature[];

  /**
   * Phase decision points.
   */
  readonly decisionPoints: Feature<Point, PhaseDecisionProperties>[];

  /** A map from each node holding a phase decision to its decision point. */
  private readonly decisionsByNode = new Map<string, Feature<Point, PhaseDecisionProperties>>();

  /** A map from each radial wire inside a section to that section. */
  private readonly sectionByEdgeId = new Map<string, Section>();

  /** A map from each node inside a section (its root included) to that section. */
  private readonly sectionByNodeId = new Map<string, Section>();

  /**
   * @param primaryWires - features of the `output/primary_wires` layer
   * @param transformers - features of the `output/transformers` layer
   * @param sources - features of the `output/sources` layer
   */
  constructor(primaryWires: Feature[], transformers: Feature[], sources: Feature[]) {
    this.radialEdges = radialPrimaryEdges(primaryWires);
    const headPhases = buildHeadPhases(this.radialEdges, sources);

    const edgesByStart = Map.groupBy(
      this.radialEdges.filter((edge) => typeof edge.properties?.StartID === "string"),
      (edge) => edge.properties?.StartID as string
    );

    const tagEdge = (edge: Feature, section: Section) => {
      const fondId = edge.properties?.FondID;
      if (typeof fondId === "string") this.sectionByEdgeId.set(fondId, section);
    };

    // Find each section's boundary edge, then tag the wires of its run and the nodes they reach.
    const boundaryStartIds = new Map<Section, string>();
    for (const edge of this.radialEdges) {
      const { StartID: startId, EndID: endId, Phase: phase } = edge.properties ?? {};
      // a section starts where a wire carries fewer phases than the wires feeding it
      const startsSection =
        typeof startId === "string" &&
        typeof endId === "string" &&
        isOverridePhase(phase) &&
        phase.length === numPhases(edge) &&
        headPhases(startId) > numPhases(edge);

      // eslint-disable-next-line no-continue
      if (!startsSection) continue;

      const coordinates = getEndVertex(edge);
      // eslint-disable-next-line no-continue
      if (!coordinates) continue;

      const section: Section = { rootId: endId, width: phase.length as 1 | 2, solvePhase: phase, parent: undefined };
      boundaryStartIds.set(section, startId);
      this.decisionsByNode.set(endId, decisionPoint(endId, phase, PHASE_DECISION_TYPES.sectionRoot, coordinates));

      tagEdge(edge, section);
      this.sectionByNodeId.set(endId, section);
      forEachDownstreamEdge(edgesByStart, endId, section.width, (downstream, downstreamEndId) => {
        tagEdge(downstream, section);
        this.sectionByNodeId.set(downstreamEndId, section);
      });
    }

    // Link each section to the section its boundary edge leaves from (undefined = the 3-phase trunk).
    for (const [section, startId] of boundaryStartIds) {
      section.parent = this.sectionByNodeId.get(startId);
    }

    for (const transformer of transformers) {
      const { FondID: fondId, Phase: phase } = transformer.properties ?? {};
      const geometry = transformer.geometry.type === "Point" ? transformer.geometry : undefined;
      // case (ii): single phase transformers sitting on a >=2-phase wire.
      // This replaces a section decision at the same node: the solver only supports single-phase
      // overrides at transformer nodes, even when the transformer sits on a section root.
      if (typeof fondId === "string" && isSinglePhase(phase) && geometry && headPhases(fondId) >= 2) {
        this.decisionsByNode.set(fondId, decisionPoint(fondId, phase, PHASE_DECISION_TYPES.transformer, geometry.coordinates));
      }
    }

    // ensure decision points are deterministic
    this.decisionPoints = [...this.decisionsByNode.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([, feature]) => feature);
  }

  /**
   * Return whether any decision point accepts a pair override (AB, AC, or BC).
   */
  get hasPairDecisions(): boolean {
    return this.decisionPoints.some((point) => this.getOverridableSectionFromRootNode(point.properties.FondID)?.width === 2);
  }

  /**
   * Return the section whose phase can be overridden at the given node, if any.
   *
   * A section's phase cannot be overridden if a single-phase transformer is collocated with the section's root node,
   * due to solver limitations.
   */
  private getOverridableSectionFromRootNode(nodeId: string): Section | undefined {
    const decision = this.decisionsByNode.get(nodeId);
    return decision?.properties.DecisionType === PHASE_DECISION_TYPES.sectionRoot ? this.sectionByNodeId.get(nodeId) : undefined;
  }

  /**
   * Return the phases a section currently carries, given the provided override features.
   *
   * The trunk section (corresponding to undefined) carries all three phases.
   */
  private currentPhases(section: Section | undefined, overridePoints: Feature[]): PhaseSet {
    if (!section) return ALL_PHASES;
    const rootOverride = overridePoints.find((point) => point.properties?.FondID === section.rootId)?.properties?.Phase;
    return isOverridePhase(rootOverride) && rootOverride.length === section.width ? rootOverride : section.solvePhase;
  }

  /** Return whether `nodeId`'s phase choices derive (directly or transitively) from the given section. */
  private isConstrainedBy(nodeId: string, section: Section): boolean {
    for (let current = this.sectionByNodeId.get(nodeId); current; current = current.parent) {
      if (current === section) return true;
    }
    return false;
  }

  /**
   * Computes the phase each radial primary wire is expected to carry once phase overrides are applied.
   *
   * @param overridePoints - the current phase override point features
   * @returns feature id -> phase for the wire
   */
  previewPhases(overridePoints: Feature[]): Map<string | number, OverridePhase> {
    const overridesByNode = new Map<string, OverridePhase>();
    for (const point of overridePoints) {
      const { FondID: fondId, Phase: phase } = point.properties ?? {};
      if (typeof fondId === "string" && isOverridePhase(phase)) {
        overridesByNode.set(fondId, phase);
      }
    }

    // transport the phase from each section root's override point to every wire of the section
    const previewPhases = new Map<string | number, OverridePhase>();
    for (const edge of this.radialEdges) {
      const fondId = edge.properties?.FondID;
      const section = typeof fondId === "string" ? this.sectionByEdgeId.get(fondId) : undefined;
      const overridePhase = section ? overridesByNode.get(section.rootId) : undefined;
      // a wire only previews a phase whose width matches the wire
      if (edge.id != null && overridePhase && overridePhase.length === numPhases(edge) && overridePhase !== edge.properties?.Phase) {
        previewPhases.set(edge.id, overridePhase);
      }
    }
    return previewPhases;
  }

  /**
   * Return the override phases a decision point at the given node may take.
   */
  allowedPhases(nodeId: string, overridePoints: Feature[]): ReadonlySet<OverridePhase> {
    // a section root chooses which of its parent section's phases the section carries
    const section = this.getOverridableSectionFromRootNode(nodeId);
    if (section) return new Set(subsetsOfSize(this.currentPhases(section.parent, overridePoints), section.width));

    // a transformer chooses its phase from the phases present at its node
    if (this.decisionsByNode.get(nodeId)?.properties.DecisionType === PHASE_DECISION_TYPES.transformer) {
      return new Set(subsetsOfSize(this.currentPhases(this.sectionByNodeId.get(nodeId), overridePoints), 1));
    }

    return new Set(SINGLE_PHASES);
  }

  /**
   * Return the set of override phases that are allowed for every feature in `selectedFeatures`.
   *
   * If the features have no phases in common then an empty set is returned.
   */
  allowedPhasesForSelection(selectedFeatures: Feature[], overridePoints: Feature[]): ReadonlySet<OverridePhase> {
    const allowedSets = selectedFeatures.map((feature) => {
      const fondId = feature.properties?.FondID;
      return typeof fondId === "string" ? this.allowedPhases(fondId, overridePoints) : new Set<OverridePhase>(SINGLE_PHASES);
    });
    if (allowedSets.length === 0) return new Set();
    return allowedSets.reduce((intersection, set) => new Set([...intersection].filter((phase) => set.has(phase))));
  }

  /**
   * Return the downstream overrides that must change when a section's phase changes.
   *
   * When a section's phase changes (e.g. "AB" to "AC"), we need to fix up any phases that
   * are no longer present (e.g. by replacing "B" with "C").
   *
   * @returns override point feature id -> remapped phase
   */
  private sectionChangeRemaps(overridePoints: Feature[], section: Section, newPhase: OverridePhase): Map<string | number, OverridePhase> {
    const remaps = new Map<string | number, OverridePhase>();

    const oldPhase = this.currentPhases(section, overridePoints);
    if (oldPhase === newPhase) return remaps;

    // each letter the section loses is replaced by the letter it gains, in alphabetical order
    const removed = phaseLetters(oldPhase).filter((letter) => !newPhase.includes(letter));
    const added = phaseLetters(newPhase).filter((letter) => !oldPhase.includes(letter));
    const replacements = new Map(removed.map((letter, index) => [letter, added[index]]));

    for (const point of overridePoints) {
      const { FondID: fondId, Phase: phase } = point.properties ?? {};
      if (
        point.id != null &&
        typeof fondId === "string" &&
        fondId !== section.rootId &&
        isOverridePhase(phase) &&
        this.isConstrainedBy(fondId, section)
      ) {
        const remapped = phaseLetters(phase)
          .map((letter) => replacements.get(letter) ?? letter)
          .sort()
          .join("");
        if (remapped !== phase && isOverridePhase(remapped)) remaps.set(point.id, remapped);
      }
    }
    return remaps;
  }

  /**
   * Return a map of changes that must be made to update the phase of the selected features to the given value.
   *
   * This handles any downstream phase changes that must be made to keep everything consistent. For example,
   * if the phase of a 2-phase wire changes from AB -> AC, then we must ensure that any downstream "B" phases
   * are replaced by "C" phases.
   *
   *  @returns override point feature id -> phase
   */
  phaseEdits(selectedFeatures: Feature[], overridePoints: Feature[], phase: OverridePhase): Map<string | number, OverridePhase> {
    const edits = new Map<string | number, OverridePhase>();

    for (const feature of selectedFeatures) {
      const fondId = feature.properties?.FondID;
      const section = typeof fondId === "string" ? this.getOverridableSectionFromRootNode(fondId) : undefined;
      if (section && section.width === phase.length) {
        this.sectionChangeRemaps(overridePoints, section, phase).forEach((remapped, id) => edits.set(id, remapped));
      }
    }

    for (const feature of selectedFeatures) {
      if (feature.id != null) edits.set(feature.id, phase);
    }

    return edits;
  }
}
